import {
	Footer,
	FooterAlt,
	FooterLite,
	Header,
	FooterVariant,
	HeaderAspen,
	HeaderClearChoice2,
	Teletriage,
	useFacilityContext,
	isOfficeClosed,
} from '@aspendental/shared-components-web';
import { IBanner } from '@aspendental/shared-components-web/lib/components/Banner/Banner';
import { IGoogleMap } from '@aspendental/shared-components-web/lib/components/GoogleMap/GoogleMap';
import { isPathMatched } from '@aspendental/shared-components-web/lib/utils';
import {
	PATIENT_PRISM_BASE_PHONE,
	PATIENT_PRISM_BASE_PHONE_DISPLAY,
	PATIENT_PRISM_BASE_PHONE_TEL,
} from '@aspendental/web-shared-utils';
import { usePathname } from 'next/navigation';
import { ReactNode } from 'react';

import { AdFooter, App, Maybe, NavigationLink, Page } from '@src/lib/__generated/sdk';

interface LayoutPropsInterface {
	children: ReactNode;
	appData: Maybe<App>;
	footerData: Maybe<AdFooter>;
	isFooterLite?: boolean;
	isFooterAlt?: boolean;
	withSimpleLayout?: boolean;
	locale: string;
	preview: boolean;
	showHeaderNavMenu: boolean;
	breadcrumbsData: Page;
	locationNavigationLinks?: Array<NavigationLink | Page>;
	withFixedHeader: boolean;
	disableStickyFooterOnMobile: boolean | null;
	disableStickyFooterOnDesktop: boolean | null;
	disableHeaderTopAlertBanner: boolean | null;
	disableHeaderBottomAlertBanner: boolean | null;
	hideLocation?: boolean;
	hidePrimaryActionButton?: boolean;
	logoHref?: string;
	showAspen2Header?: boolean;
	showClearChoice2Header?: boolean;
	hideNavigation?: boolean;
	/**
	 * VWO `ccPatientPrismPhoneSwap` 1% treatment: render the Patient Prism pool
	 * number (`PATIENT_PRISM_BASE_PHONE`) in the national header CTA so PP swaps
	 * it for a dynamic tracking number instead of Invoca. Resolved server-side
	 * (`serverFeatureFlags`) in _app.page.tsx; defaults to the Contentful number.
	 * The legacy header only swaps a Contentful phone with nonblank display text.
	 * Facility/Teletriage numbers are never swapped.
	 */
	phoneSwapOn?: boolean;
	/**
	 * VWO `ghostPrimaryScheduleButton` (TTB-322) treatment: render the ClearChoice2
	 * header's primary CTA as a lower-emphasis "ghost" button. Resolved server-side
	 * (`serverFeatureFlags`) in _app.page.tsx; defaults to the standard CTA.
	 */
	ghostPrimaryScheduleButton?: boolean;
}

function getHideStickyFooterPaths(appData: Maybe<App>): string[] {
	const paths = (appData?.config as { hideStickyFooter?: unknown } | null | undefined)?.hideStickyFooter;

	return Array.isArray(paths) ? paths.filter((path): path is string => typeof path === 'string') : [];
}

function getHideHeaderPaths(appData: Maybe<App>): string[] {
	const paths = (appData?.config as { hideHeader?: unknown } | null | undefined)?.hideHeader;

	return Array.isArray(paths) ? paths.filter((path): path is string => typeof path === 'string') : [];
}

function getHideFooterPaths(appData: Maybe<App>): string[] {
	const paths = (appData?.config as { hideFooter?: unknown } | null | undefined)?.hideFooter;

	return Array.isArray(paths) ? paths.filter((path): path is string => typeof path === 'string') : [];
}

function getFooter(
	footerData: Maybe<AdFooter>,
	isFooterLite: boolean,
	isFooterAlt: boolean,
	withSimpleLayout: boolean,
	breadcrumbsData: Page | undefined,
	disableStickyFooterOnMobile: boolean | null,
	disableStickyFooterOnDesktop: boolean | null,
	phoneSwapOn: boolean
) {
	if (isFooterLite) {
		return (
			<FooterLite
				disclaimer={footerData?.disclaimer as any}
				footerData={footerData as any}
				breadcrumbsData={breadcrumbsData as any}
			/>
		);
	}

	if (isFooterAlt) {
		return <FooterAlt footerData={footerData as any} />;
	}

	if (footerData?.variant === 'teamTag') {
		return (
			<FooterVariant
				footerData={footerData as any}
				withScheduling={withSimpleLayout}
				breadcrumbsData={breadcrumbsData as any}
				isLightFooter={!!footerData?.isLightFooter}
			/>
		);
	}

	return (
		<Footer
			footerData={footerData as any}
			withScheduling={withSimpleLayout}
			breadcrumbsData={breadcrumbsData as any}
			isLightFooter={!!footerData?.isLightFooter}
			disableStickyFooterOnMobile={disableStickyFooterOnMobile}
			disableStickyFooterOnDesktop={disableStickyFooterOnDesktop}
			phoneSwapNumber={phoneSwapOn ? PATIENT_PRISM_BASE_PHONE : undefined}
		/>
	);
}

export const Layout = ({
	appData,
	footerData,
	isFooterLite = true,
	isFooterAlt = false,
	withSimpleLayout = false,
	children,
	showHeaderNavMenu,
	breadcrumbsData,
	locationNavigationLinks,
	withFixedHeader,
	disableStickyFooterOnMobile,
	disableStickyFooterOnDesktop,
	hideLocation = false,
	hidePrimaryActionButton = false,
	disableHeaderTopAlertBanner,
	disableHeaderBottomAlertBanner,
	logoHref,
	showAspen2Header = false,
	showClearChoice2Header = false,
	hideNavigation = false,
	phoneSwapOn = false,
	ghostPrimaryScheduleButton = false,
}: LayoutPropsInterface) => {
	let headerComponent: ReactNode;
	const pathname = usePathname();
	const hideHeaderOnPath = isPathMatched(pathname, getHideHeaderPaths(appData));
	const hideFooterOnPath = isPathMatched(pathname, getHideFooterPaths(appData));
	const { officeInfo } = useFacilityContext();
	const workingHours = officeInfo?.workingHours;
	const teletriagePhoneNumber = officeInfo?.technicalInfrastructure?.phoneNumber?.trim() || null;
	const teletriageValue =
		officeInfo?.attributes
			?.find((attribute) => attribute?.name === 'Teletriage Enabled')
			?.value?.trim()
			?.toLowerCase() ?? null;
	const teletriageEnabled = teletriageValue === 'yes' || teletriageValue === 'true';
	const hasOfficeHours = !!workingHours;
	const officeIsClosed = hasOfficeHours ? isOfficeClosed(workingHours as any) : false;
	if (showAspen2Header) {
		headerComponent = (
			<HeaderAspen
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				headerData={appData?.header as any}
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				logo={appData?.logo as any}
				disableStickyFooterPaths={getHideStickyFooterPaths(appData)}
			/>
		);
	} else if (showClearChoice2Header || hideNavigation) {
		headerComponent = (
			<HeaderClearChoice2
				headerData={appData?.header as any}
				logo={appData?.logo as any}
				topAlertBanner={appData?.header?.topAlertBanner as IBanner}
				bottomAlertBanner={appData?.header?.bottomAlertBanner as IBanner}
				disableHeaderTopAlertBanner={disableHeaderTopAlertBanner}
				disableHeaderBottomAlertBanner={disableHeaderBottomAlertBanner}
				phoneSwapNumber={phoneSwapOn ? PATIENT_PRISM_BASE_PHONE : undefined}
				ghostPrimaryScheduleButton={ghostPrimaryScheduleButton}
				{...({ hideNavigation } as any)}
			/>
		);
	} else {
		const configuredPhone = appData?.header?.phoneNumber;
		let phoneNumber = configuredPhone?.linkText?.trim() ? configuredPhone : null;
		if (phoneNumber && phoneSwapOn) {
			phoneNumber = {
				...phoneNumber,
				linkText: PATIENT_PRISM_BASE_PHONE_DISPLAY,
				externalUrl: PATIENT_PRISM_BASE_PHONE_TEL,
			};
		}

		headerComponent = (
			<Header
				locationNavigationLinks={locationNavigationLinks as any}
				appData={appData as any}
				logoHref={logoHref}
				withOfficeDetails
				showHeaderNavMenu={showHeaderNavMenu}
				phoneNumber={phoneNumber}
				withFixedHeader={withFixedHeader}
				useHeaderLocationCards={appData?.header?.useHeaderLocationCards as any}
				hideLocation={hideLocation}
				hidePrimaryActionButton={hidePrimaryActionButton}
				googleMapsForLocationCards={appData?.header?.googleMapsForLocationCards as IGoogleMap}
				topAlertBanner={appData?.header?.topAlertBanner as IBanner}
				bottomAlertBanner={appData?.header?.bottomAlertBanner as IBanner}
				disableHeaderTopAlertBanner={disableHeaderTopAlertBanner}
				disableHeaderBottomAlertBanner={disableHeaderBottomAlertBanner}
			/>
		);
	}

	return (
		<>
			{appData && !hideHeaderOnPath && <>{headerComponent}</>}
			{children}
			{teletriageEnabled && hasOfficeHours && officeIsClosed && teletriagePhoneNumber && (
				<Teletriage phoneNumber={teletriagePhoneNumber} />
			)}
			{footerData &&
				!hideFooterOnPath &&
				getFooter(
					footerData,
					isFooterLite,
					isFooterAlt,
					withSimpleLayout,
					breadcrumbsData,
					disableStickyFooterOnMobile,
					disableStickyFooterOnDesktop,
					phoneSwapOn
				)}
		</>
	);
};
